import { gql } from '@apollo/client'

export const RETRIEVE_CAFE_CONFIG = gql`
    query RetrieveCafe($cafeId: Int) {
        retrieveCafe(id: $cafeId) {
            result
            error
            cafe {
                id
                name
                note
                stalls {
                    id
                    name
                    note
                    vendorId
                }
                tables {
                    id
                    name
                    note
                }
                cashRegisters {
                    id
                    name
                    note
                }
            }
        }
    }
`

export const RETRIEVE_CAFE_CASH_REGISTERS = gql`
    query RetrieveCafeCashRegisters($cafeId: Int) {
        retrieveCafeCashRegisters(cafeId: $cafeId) {
            id
            cafeId
            name
            note
        }
    }
`

export const RETRIEVE_CAFE_GROUPS = gql`
    query RetrieveCafeGroups($cafeId: Int) {
        retrieveCafeGroups(cafeId: $cafeId) {
            id
            type
            name
        }
    }
`

export const RETRIEVE_MENUS = gql`
    query RetrieveMenus($input: MenuInput!) {
        retrieveMenus(input: $input) {
            result
            error
            menus {
                id
                dishId
                name
                description
                note
                tags {
                    id
                    name
                }
                stallId
                stallName
                vendorId
                vendorName
                isInStoreOnly
                createdAt
                updatedAt
                openTimes {
                    name
                    times {
                        open
                        close
                    }
                }
                sessions {
                    id
                    name
                }
                variants {
                    id
                    name
                    price
                    sellPrice
                    inventory {
                        id
                        status
                        initialQuantity
                        orderedQuantity
                    }
                    selections {
                        id
                        name
                        type
                        quantity
                        isRequired
                        options {
                            id
                            name
                            sellPrice
                            maxQuantity
                            inventory {
                                id
                                status
                                initialQuantity
                                orderedQuantity
                            }
                        }
                    }
                }
            }
            pageInfo {
                totalCount
                currentPage
                totalPages
                hasNextPage
                hasPreviousPage
            }
        }
    }
`

export const CREATE_ORDER = gql`
    mutation CreateOrder($input: CreateOrderInput!) {
        createOrder(input: $input) {
            result
            error
            order {
                orderId
                number
                cafeId
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                note
                isDineIn
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                    freeCount
                    discountedLineItemIds
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeId
                    note
                    selections {
                        id
                        name
                        type
                        quantity
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                    }
                }
            }
            promoResult {
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                }
                rejectedPromos {
                    promoType
                    displayName
                    reason
                    message
                }
            }
        }
    }
`

export const CREATE_MEMBER_ORDER = gql`
    mutation CreateMemberOrder($input: CreateMemberOrderInput!) {
        createMemberOrder(input: $input) {
            result
            error
            order {
                orderId
                number
                cafeId
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                memberId
                pickupScheduledAt
                note
                isDineIn
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                    freeCount
                    discountedLineItemIds
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeStallName
                    vendorId
                    vendorName
                    cafeId
                    note
                    selections {
                        id
                        type
                        quantity
                        name
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                    }
                }
            }
            promoResult {
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                }
                rejectedPromos {
                    promoType
                    displayName
                    reason
                    message
                }
            }
        }
    }
`

export const RETRIEVE_ORDERS = gql`
    query RetrieveOrders($input: RetrieveOrderInput!) {
        retrieveOrders(input: $input) {
            result
            error
            orders {
                orderId
                number
                cafeId
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                note
                isDineIn
                memberId
                pickupScheduledAt
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                    freeCount
                    discountedLineItemIds
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeStallName
                    vendorId
                    vendorName
                    cafeId
                    note
                    selections {
                        id
                        name
                        type
                        quantity
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                        pickupSlotNumber
                    }
                }
            }
        }
    }
`

export const RETRIEVE_ORDER_BY_NUMBER = gql`
    query RetrieveOrderByNumber($input: RetrieveOrderInput!) {
        retrieveOrderByNumber(input: $input) {
            result
            error
            order {
                orderId
                number
                cafeId
                type
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                memberId
                pickupScheduledAt
                note
                isDineIn
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                    freeCount
                    discountedLineItemIds
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeStallName
                    vendorId
                    vendorName
                    cafeId
                    note
                    pendingOrdersAhead
                    selections {
                        id
                        type
                        quantity
                        name
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                        pickupSlotNumber
                    }
                }
            }
        }
    }
`

export const AVAILABLE_PROMOS = gql`
    query AvailablePromos {
        availablePromos {
            promoType
            enabled
            displayName
            cafeIds
            eligibleDishIds
            eligibleTagIds
            timeWindows {
                start
                end
            }
            weekdays
            stallId
        }
    }
`

export const CHECK_INVENTORY_STATUS = gql`
    query CheckInventoryStatus($ids: [Int]!) {
        checkInventoryStatus(ids: $ids) {
            result
            error
            inventories {
                id
                status
                initialQuantity
                orderedQuantity
            }
        }
    }
`

export const CREATE_PAYMENT_INTENT = gql`
    mutation CreatePaymentIntent($input: CreatePaymentIntentInput!) {
        createPaymentIntent(input: $input) {
            result
            error
            paymentIntent {
                id
                amount
                currency
                status
                metadata {
                    orderId
                }
                client_secret
                created
                updated
                next_action {
                    type
                    paynow_display_qr_code {
                        hosted_instructions_url
                        image_url_png
                        image_url_svg
                        data
                    }
                }
            }
        }
    }
`

export const CHECK_ORDER_PAYMENT_STATUS = gql`
    query CheckOrderPaymentStatus($orderId: String!) {
        checkOrderPaymentStatus(orderId: $orderId) {
            result
            error
            order {
                orderId
                number
                status
                payment {
                    id
                    type
                    paymentIntentId
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                note
                isDineIn
                pickupScheduledAt
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                appliedPromos {
                    promoType
                    displayName
                    discountAmount
                    freeCount
                    discountedLineItemIds
                }
            }
        }
    }
`

// Membership GraphQL Queries and Mutations
export const REGISTER_MEMBER = gql`
    mutation RegisterMember($input: RegisterInput!) {
        registerMember(input: $input) {
            result
            error
        }
    }
`

export const REQUIRE_OTP = gql`
    mutation RequireOTP($input: RequireOTPInput!) {
        requireOTP(input: $input) {
            result
            error
            nextAvailableTime
        }
    }
`

export const VERIFY_EMAIL_OTP = gql`
    mutation VerifyEmailOTP($input: VerifyEmailOTPInput!) {
        verifyEmailOTP(input: $input) {
            result
            error
        }
    }
`

export const GET_MEMBER_ACCESS_TOKEN = gql`
    mutation GetMemberAccessToken($input: GetTokenInput!) {
        getMemberAccessToken(input: $input) {
            result
            error
            errorCode
            accessToken
            refreshToken
            expiresAt
        }
    }
`

export const REFRESH_ACCESS_TOKEN = gql`
    mutation RefreshAccessToken($input: RefreshTokenInput!) {
        refreshAccessToken(input: $input) {
            result
            error
            errorCode
            accessToken
            refreshToken
            expiresAt
        }
    }
`

export const REVOKE_REFRESH_TOKEN = gql`
    mutation RevokeRefreshToken($input: RefreshTokenInput!) {
        revokeRefreshToken(input: $input) {
            result
            error
            errorCode
        }
    }
`

export const GET_CURRENT_MEMBER = gql`
    query GetCurrentMember {
        getCurrentMember {
            result
            error
            cafeMembers {
                id
                name
                phone
                email
                status
                createdAt
                updatedAt
                benefit {
                    id
                    discountPct
                    isRequireNote
                    scope
                    cafeId
                    stallId
                    appliesTo
                    effectiveFrom
                    effectiveTo
                }
                stalls {
                    id
                    cafeId
                    stallId
                    isPrimary
                }
                assignments {
                    id
                    role {
                        id
                        code
                        name
                        description
                    }
                    scope
                    cafeId
                    stallId
                    effectiveFrom
                    effectiveTo
                }
                credit {
                    totalAvailableCents
                    expiringNextWeekCents
                    availableBuckets {
                        id
                        scope
                        cafeId
                        stallId
                        forDate
                        expiresAt
                        usageRestriction
                        amountCents
                        consumedCents
                        remainingCents
                    }
                    availableByScope {
                        scope
                        cents
                    }
                    availableByUsage {
                        usageRestriction
                        cents
                    }
                }
            }
        }
    }
`

export const RETRIEVE_MEMBER_ORDER_HISTORY = gql`
    query RetrieveMemberOrderHistory($input: RetrieveMemberOrderHistoryInput!) {
        retrieveMemberOrderHistory(input: $input) {
            result
            error
            orders {
                orderId
                number
                cafeId
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                memberId
                pickupScheduledAt
                note
                isDineIn
                isToday
                servingDateTime
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeStallName
                    vendorId
                    vendorName
                    cafeId
                    note
                    selections {
                        id
                        type
                        quantity
                        name
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                        pickupSlotNumber
                    }
                }
            }
            pageInfo {
                totalCount
                currentPage
                totalPages
                hasNextPage
                hasPreviousPage
            }
        }
    }
`

export const RETRIEVE_MEMBER_UPCOMING_ORDERS = gql`
    query RetrieveMemberUpcomingOrders($input: RetrieveMemberUpcomingOrdersInput!) {
        retrieveMemberUpcomingOrders(input: $input) {
            result
            error
            orders {
                orderId
                number
                cafeId
                table {
                    id
                    name
                    note
                }
                status
                payment {
                    id
                    type
                    paymentIntentId
                    clientSecret
                    paidAt
                }
                createdOn
                createdBy
                updatedOn
                updatedBy
                memberId
                pickupScheduledAt
                note
                isDineIn
                summary {
                    originalAmount
                    benefitDiscount
                    creditDiscount
                    promoDiscount
                    finalAmount
                }
                cancellationInfo {
                    isCancellable
                    cancellationDeadline
                }
                dishes {
                    id
                    dishId
                    name
                    cafeMenuId
                    cafeStallId
                    cafeStallName
                    vendorId
                    vendorName
                    cafeId
                    note
                    selections {
                        id
                        type
                        quantity
                        name
                        options {
                            id
                            name
                            sellPrice
                            quantity
                        }
                    }
                    variants {
                        id
                        variantId
                        name
                        price
                        sellPrice
                        quantity
                        completedAt
                        pickupSlotNumber
                    }
                }
            }
            pageInfo {
                totalCount
                currentPage
                totalPages
                hasNextPage
                hasPreviousPage
            }
        }
    }
`

export const CHECK_PENDING_RATINGS = gql`
    query CheckPendingRatings {
        checkPendingRatings {
            result
            error
            hasPendingRatings
            lastOrder {
                orderId
                orderNumber
                createdOn
            }
            pendingItems {
                orderDishId
                dishName
                orderDishVariantIds
                variantName
                quantity
                selectionText
                vendorName
                orderId
                orderNumber
                orderServingOn
            }
        }
    }
`

export const SUBMIT_DISH_RATINGS = gql`
    mutation SubmitDishRatings($input: SubmitDishRatingsInput!) {
        submitDishRatings(input: $input) {
            result
            error
        }
    }
`

export const CANCEL_MEMBER_ORDER = gql`
    mutation CancelMemberOrder($input: CancelMemberOrderInput!) {
        cancelMemberOrder(input: $input) {
            result
            error
            refund {
                refundId
                amount
                status
                estimatedArrival
            }
        }
    }
`

export const REQUEST_PASSWORD_RESET = gql`
    mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
        requestPasswordReset(input: $input) {
            result
            error
            nextAvailableTime
        }
    }
`

export const VERIFY_PASSWORD_RESET_TOKEN = gql`
    mutation VerifyPasswordResetToken($input: VerifyPasswordResetTokenInput!) {
        verifyPasswordResetToken(input: $input) {
            result
            error
        }
    }
`

export const RESET_PASSWORD = gql`
    mutation ResetPassword($input: ResetPasswordInput!) {
        resetPassword(input: $input) {
            result
            error
        }
    }
`

export const RETRIEVE_EMPLOYEE_OTP = gql`
    query RetrieveEmployeeOTP($email: String!) {
        retrieveOTP(email: $email) {
            result
            error
            expiryMinutes
        }
    }
`

export const RETRIEVE_EMPLOYEE_ACCESS_TOKEN = gql`
    query RetrieveEmployeeAccessToken($email: String!, $code: String!) {
        retrieveAccessToken(email: $email, code: $code) {
            result
            error
            accessToken
            expiredAt
            employee {
                id
                cafeId
                cashRegisterId
                stallId
                role
                firstName
                lastName
                email
                phone
                createdAt
                updatedAt
                token
                isRestricted
            }
        }
    }
`

export const RETRIEVE_EMPLOYEE = gql`
    query RetrieveEmployee($accessToken: String!) {
        retrieveEmployee(accessToken: $accessToken) {
            result
            error
            employee {
                id
                cafeId
                cashRegisterId
                stallId
                role
                firstName
                lastName
                email
                phone
                createdAt
                updatedAt
                token
                isRestricted
            }
        }
    }
`
